import React, { useMemo, useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import { Listbox } from "@headlessui/react";
import { mdiArrowDown, mdiChevronDown, mdiMenuLeft, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import Checkbox from "components/Checkbox";
import { ITableHeaderList } from "models/HaulPay";
import styles from "./style.module.scss";
import { RefLoadingSpinner } from "../LoadingSpinner";
import usePaginatedInfiniteScroll from "hooks/usePaginatedInfiniteScroll";

const SimpleScrollbar = ({ containerRef }: { containerRef: React.RefObject<HTMLDivElement> }) => {
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [isNeeded, setIsNeeded] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Get an initial measurement of the table width
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef]);

  // Dedicated scroll detection effect
  useEffect(() => {
    if (!containerRef.current) return;

    const checkScrollPosition = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const tableTop = container.getBoundingClientRect().top;
      const tableElement = container.querySelector("table");

      // Update sticky state
      setIsSticky(tableTop < 0);

      // Update width when sticky - using the exact table width
      if (tableTop < 0 && tableElement) {
        // Use the actual table width, not container width
        setContainerWidth(tableElement.clientWidth);
      }
    };

    // Check scroll position on mount and on scroll
    checkScrollPosition();

    // Set up window scroll listener
    const handleScroll = () => {
      checkScrollPosition();

      // Sync horizontal scroll position when scrolling vertically
      if (isSticky && containerRef.current) {
        // Force-sync the scroll position of sticky bar to match table
        const stickyThumb = document.querySelector(".sticky-scrollbar-thumb");
        if (stickyThumb && containerRef.current) {
          const container = containerRef.current;
          const scrollRatio =
            container.scrollLeft / (container.scrollWidth - container.clientWidth);
          const thumbWidth = Math.max((container.clientWidth / container.scrollWidth) * 100, 15);
          const maxLeftOffset = 100 - thumbWidth;
          const leftPosition = scrollRatio * maxLeftOffset;
          (stickyThumb as HTMLElement).style.left = `${leftPosition}%`;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Add scroll listener to the table container to sync thumb position
    const syncStickyOnTableScroll = () => {
      if (isSticky && containerRef.current) {
        const container = containerRef.current;
        const stickyThumb = document.querySelector(".sticky-scrollbar-thumb");
        if (stickyThumb) {
          const scrollRatio =
            container.scrollLeft / (container.scrollWidth - container.clientWidth);
          const thumbWidth = Math.max((container.clientWidth / container.scrollWidth) * 100, 15);
          const maxLeftOffset = 100 - thumbWidth;
          const leftPosition = scrollRatio * maxLeftOffset;
          (stickyThumb as HTMLElement).style.left = `${leftPosition}%`;
        }
      }
    };

    containerRef.current.addEventListener("scroll", syncStickyOnTableScroll);

    // Fallback polling interval as safety net
    const intervalId = setInterval(checkScrollPosition, 500);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (containerRef.current) {
        containerRef.current.removeEventListener("scroll", syncStickyOnTableScroll);
      }
      clearInterval(intervalId);
    };
  }, [containerRef, isSticky]);

  // Main effect for scrollbar functionality
  useEffect(() => {
    const container = containerRef.current;
    const scrollbar = scrollbarRef.current;
    const thumb = thumbRef.current;

    if (!container || !scrollbar || !thumb) return;

    const updateThumbPosition = () => {
      const scrollWidthBuffer = 5; // 5px buffer
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth + scrollWidthBuffer;

      setIsNeeded(hasHorizontalScroll);

      if (!hasHorizontalScroll) return;

      const scrollRatio = container.scrollLeft / (container.scrollWidth - container.clientWidth);
      const thumbWidth = Math.max((container.clientWidth / container.scrollWidth) * 100, 15);
      thumb.style.width = `${thumbWidth}%`;

      const maxLeftOffset = 100 - thumbWidth;
      const leftPosition = scrollRatio * maxLeftOffset;
      thumb.style.left = `${leftPosition}%`;
    };

    updateThumbPosition();

    const handleScroll = () => {
      requestAnimationFrame(updateThumbPosition);
    };

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
      startScrollLeft = container.scrollLeft;
      document.body.style.userSelect = "none";

      const onMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;

        const dx = e.clientX - startX;
        const scrollRatio = dx / scrollbar.clientWidth;
        const maxScroll = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.max(
          0,
          Math.min(startScrollLeft + scrollRatio * maxScroll, maxScroll)
        );
      };

      const onMouseUp = () => {
        isDragging = false;
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    const onTrackClick = (e: MouseEvent) => {
      if (e.target !== scrollbar) return;

      const clickX = e.clientX - scrollbar.getBoundingClientRect().left;
      const scrollRatio = clickX / scrollbar.clientWidth;
      const targetScroll = scrollRatio * (container.scrollWidth - container.clientWidth);

      container.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });
    };

    container.addEventListener("scroll", handleScroll);
    thumb.addEventListener("mousedown", onMouseDown);
    scrollbar.addEventListener("click", onTrackClick);

    const resizeObserver = new ResizeObserver(() => {
      updateThumbPosition();
      setContainerWidth(container.offsetWidth);
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      thumb.removeEventListener("mousedown", onMouseDown);
      scrollbar.removeEventListener("click", onTrackClick);
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return (
    <>
      {/* Regular scrollbar that stays in place */}
      <div
        className={`relative h-6 my-2 bg-transparent rounded-md cursor-pointer mx-1 transition-opacity duration-300 ${
          isNeeded ? "opacity-100" : "opacity-0 pointer-events-none h-0 my-0"
        }`}>
        <div
          ref={scrollbarRef}
          className="relative h-6 w-full bg-transparent rounded-md cursor-pointer">
          <div
            ref={thumbRef}
            className="absolute top-1 h-3 bg-gray-500 hover:bg-gray-600 active:bg-gray-600 rounded-md cursor-grab"
            style={{ width: "20%", left: "0%" }}
          />
        </div>
      </div>

      {/* Sticky scrollbar that shows at top of screen */}
      {isSticky && isNeeded && (
        <div
          className="fixed top-0 left-0 w-full h-5 flex justify-center items-center z-[9999]"
          style={{ background: "transparent", boxShadow: "none", border: "none", padding: 0 }}>
          <div
            className="relative h-5 rounded-md cursor-pointer bg-transparent mx-1 shadow-sm"
            style={{
              width: `${containerWidth}px`,
              maxWidth: "calc(100% - 16px)"
            }}
            onClick={(e) => {
              if (!containerRef.current) return;

              const scrollbar = e.currentTarget;
              const rect = scrollbar.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const scrollRatio = clickX / rect.width;
              const container = containerRef.current;
              const targetScroll = scrollRatio * (container.scrollWidth - container.clientWidth);

              container.scrollTo({
                left: targetScroll,
                behavior: "smooth"
              });
            }}>
            <div
              className="sticky-scrollbar-thumb absolute top-1 h-3 bg-gray-500 hover:bg-gray-600 active:bg-gray-600 rounded-md cursor-grab"
              style={{
                width: containerRef.current
                  ? `${Math.max(
                      (containerRef.current.clientWidth / containerRef.current.scrollWidth) * 100,
                      15
                    )}%`
                  : "20%",
                left: containerRef.current
                  ? `${
                      (containerRef.current.scrollLeft /
                        (containerRef.current.scrollWidth - containerRef.current.clientWidth)) *
                      (100 -
                        Math.max(
                          (containerRef.current.clientWidth / containerRef.current.scrollWidth) *
                            100,
                          15
                        ))
                    }%`
                  : "0%"
              }}
              onMouseDown={(e) => {
                if (!containerRef.current) return;

                const container = containerRef.current;
                let isDragging = true;
                const startX = e.clientX;
                const startScrollLeft = container.scrollLeft;
                document.body.style.userSelect = "none";

                // Store the scrollbar element reference here, before any async operations
                const scrollbarElement = e.currentTarget.parentElement;
                if (!scrollbarElement) return;

                const onMouseMove = (moveEvent: MouseEvent) => {
                  if (!isDragging || !containerRef.current) return;

                  const container = containerRef.current;
                  const dx = moveEvent.clientX - startX;
                  const scrollRatio = dx / scrollbarElement.clientWidth;
                  const maxScroll = container.scrollWidth - container.clientWidth;
                  container.scrollLeft = Math.max(
                    0,
                    Math.min(startScrollLeft + scrollRatio * maxScroll, maxScroll)
                  );
                };

                const onMouseUp = () => {
                  isDragging = false;
                  document.body.style.userSelect = "";
                  document.removeEventListener("mousemove", onMouseMove);
                  document.removeEventListener("mouseup", onMouseUp);
                };

                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);

                e.preventDefault();
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

interface ExpandableViewProps {
  row: Record<string, any>;
}

interface IProps {
  headers: ITableHeaderList[];
  data: Array<Record<string, any>>;
  totalCount?: number;
  nextLink?: boolean;
  prevLink?: boolean;
  handlePageChange?: (itemsPerPage: number, pageNumber?: number) => void;
  handleSelectAll?: VoidFunction;
  selectedList?: string[];
  errorList?: string[];
  handleClick?: (id: string) => void;
  onPrimaryClick?: (headerValue: string | string[], cellValue: any) => void;
  onSecondaryClick?: (headerValue: string | string[], cellValue: any) => void;
  sortDisableList?: string[];
  isShowChecked?: boolean;
  tableBodyClass?: string;
  className?: string;
  fetchNextPage?: () => void;
  fetchPrevPage?: () => void;
  setPageSize?: (pageSize: number) => void;
  goToPage?: (pageNumber: number) => void;
  loading?: boolean;
  infiniteScroll?: boolean;
  ExpandableView?: React.FC<ExpandableViewProps>;
  simple?: boolean;
  handleSortBy?: (sortKey: string) => void;
  currentSortKey?: string;
  loadMore?: boolean;
  itemPerPageOptions?: number[];
  initialItemsPerPage?: number;
  selectAllLimit?: number;
  customSelectAllCheckbox?: React.FC;
  customTooltipCheckbox?: React.FC;
  isDisableCheckboxes?: boolean;
  rowWidth?: string | number;
}

export default function DataTable({
  headers,
  data,
  handleSelectAll,
  selectedList,
  errorList,
  totalCount,
  nextLink = false,
  prevLink = false,
  handlePageChange,
  handleClick,
  onPrimaryClick,
  onSecondaryClick,
  sortDisableList,
  isShowChecked = false,
  tableBodyClass,
  className,
  fetchPrevPage,
  fetchNextPage,
  setPageSize,
  goToPage,
  loading = false,
  infiniteScroll,
  ExpandableView,
  simple,
  loadMore = false,
  handleSortBy,
  currentSortKey,
  itemPerPageOptions = [50, 100],
  initialItemsPerPage = 50,
  selectAllLimit,
  customSelectAllCheckbox,
  customTooltipCheckbox,
  isDisableCheckboxes,
  rowWidth
}: Readonly<IProps>) {
  const dataLength = data.length;
  const [sortKey, setSortKey] = useState(currentSortKey ?? "");
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  // Sync sortKey with controlled currentSortKey prop
  useEffect(() => {
    if (currentSortKey !== undefined) {
      setSortKey(currentSortKey);
    }
  }, [currentSortKey]);
  const [pageIndex, setPageIndex] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  const [expand, setExpand] = useState<Record<number, boolean>>({});

  const handleLoadMore = useCallback(async () => {
    if (fetchNextPage && nextLink) {
      setIsLoadingMore(true);
      await fetchNextPage();
      setIsLoadingMore(false);
    }
  }, [fetchNextPage, nextLink]);

  const { containerRef, loadMoreRef } = usePaginatedInfiniteScroll({
    loadMore: handleLoadMore,
    hasMore: nextLink,
    loading: isLoadingMore || loading,
    threshold: 300
  });

  const topScrollRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState<number>(0);

  useLayoutEffect(() => {
    if (!tableRef.current) {
      requestAnimationFrame(() => {
        if (tableRef.current) {
          const table = tableRef.current;
          const width = table.getBoundingClientRect().width;
          if (width > 0) {
            setTableWidth(width);
          }
        }
      });
      return;
    }

    const table = tableRef.current;
    const setWidth = () => {
      const width = table.getBoundingClientRect().width;
      if (width > 0) {
        setTableWidth(width);
      }
    };

    setTimeout(setWidth, 100);

    const ro = new ResizeObserver(() => {
      setTimeout(setWidth, 100);
    });

    ro.observe(table);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const topScroll = topScrollRef.current;
    const contentScroll = contentScrollRef.current;

    if (!topScroll || !contentScroll) return;

    const handleTopScroll = () => {
      if (contentScroll) {
        contentScroll.scrollLeft = topScroll.scrollLeft;
      }
    };

    const handleContentScroll = () => {
      if (topScroll) {
        topScroll.scrollLeft = contentScroll.scrollLeft;
      }
    };

    const updateScrollbarWidth = () => {
      if (tableWidth && topScroll.firstElementChild) {
        (topScroll.firstElementChild as HTMLElement).style.width = `${tableWidth}px`;
      }
    };

    updateScrollbarWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollbarWidth();
    });

    if (contentScroll.querySelector("table")) {
      resizeObserver.observe(contentScroll.querySelector("table") as HTMLElement);
    }

    topScroll.addEventListener("scroll", handleTopScroll);
    contentScroll.addEventListener("scroll", handleContentScroll);

    return () => {
      topScroll.removeEventListener("scroll", handleTopScroll);
      contentScroll.removeEventListener("scroll", handleContentScroll);
      resizeObserver.disconnect();
    };
  }, [tableWidth]);

  useLayoutEffect(() => {
    const loadingSpinnerElement = document.getElementById("loadingSpinner");
    if (loadingSpinnerElement) {
      const width =
        tableWidth > 0 ? tableWidth : tableRef.current?.getBoundingClientRect().width || 0;
      if (width > 0) {
        loadingSpinnerElement.style.width = `${width}px`;
      } else {
        loadingSpinnerElement.style.width = "100%";
      }
    }
  }, [tableWidth, loading]);

  useEffect(() => {
    if (isSorting && !loading) {
      setIsSorting(false);
    }
  }, [loading, isSorting]);

  function handleChecked(id: string) {
    if (handleClick) {
      handleClick(id);
    }
  }

  const isSelected = (id: string): boolean | undefined => {
    if (selectedList) {
      return selectedList.includes(id);
    }
    return false;
  };

  function getHeaderSortKey(header: ITableHeaderList): string {
    return header.dbSortName ?? (Array.isArray(header.value) ? header.value[0] : header.value);
  }

  function sortBy(key: string) {
    if (!sortDisableList?.includes(key)) {
      let newSortKey: string;
      if (sortKey === key) {
        // Currently ascending, switch to descending
        newSortKey = "-" + key;
      } else if (sortKey === "-" + key) {
        // Currently descending, clear sort
        newSortKey = "";
      } else {
        // No sort or different column, set ascending
        newSortKey = key;
      }
      setSortKey(newSortKey);

      if (handleSortBy && totalPageNumbers > 0) {
        setIsSorting(true);
        handleSortBy(newSortKey);
      }
    }
  }

  function getNestedValue(row: any, keyPath: string): any {
    const keys = keyPath.split(".");
    let nestedAcc = row;

    for (const key of keys) {
      nestedAcc = nestedAcc?.[key];
      if (nestedAcc === undefined) {
        return undefined;
      }
    }

    return nestedAcc;
  }

  const totalPageNumbers = useMemo(
    () => (totalCount === 0 ? 1 : Math.ceil((totalCount ?? 1) / itemsPerPage)),
    [itemsPerPage, totalCount]
  );

  const { startIndex, lastIndex } = useMemo(() => {
    if (dataLength === 0) return { startIndex: 0, lastIndex: 0 };

    if (totalCount && itemsPerPage > totalCount) setPageIndex(1);

    const startIndex = (pageIndex - 1) * itemsPerPage + 1;
    let lastIndex;
    if (pageIndex === totalPageNumbers) {
      lastIndex = totalCount ? totalCount : dataLength;
    } else {
      lastIndex = itemsPerPage * pageIndex;
    }

    return { startIndex, lastIndex };
  }, [dataLength, itemsPerPage, pageIndex, totalCount, totalPageNumbers]);

  const primaryClick = (value: Record<string, any> | string, header: ITableHeaderList) => {
    return onPrimaryClick ? () => onPrimaryClick(header.value, value) : () => {};
  };

  const secondaryClick = (value: Record<string, any> | string, header: ITableHeaderList) => {
    return onSecondaryClick ? () => onSecondaryClick(header.value, value) : () => {};
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPageIndex(1);
    if (handlePageChange && totalCount)
      handlePageChange(value < totalCount ? value : totalCount, 1);
  };

  function goNextPage() {
    if (nextLink && handlePageChange) {
      setPageIndex(pageIndex + 1);
      handlePageChange(itemsPerPage, pageIndex + 1);
    }
    if (fetchNextPage) {
      setPageIndex(pageIndex + 1);
      fetchNextPage();
    }
  }

  function goPreviousPage() {
    if (prevLink && handlePageChange) {
      setPageIndex(pageIndex - 1);
      handlePageChange(itemsPerPage, pageIndex - 1);
    }
    if (fetchPrevPage) {
      setPageIndex(pageIndex - 1);
      fetchPrevPage();
    }
  }

  if (simple) {
    const visibleHeaders = headers.filter((header) => header.isShow !== false);
    const normalHeaders = visibleHeaders.filter((header) => !header.fullHorizontal);
    const fullHorizontalHeaders = visibleHeaders.filter((header) => header.fullHorizontal);

    const contentScrollRef = useRef<HTMLDivElement>(null);

    return (
      <div className="w-full">
        <SimpleScrollbar containerRef={contentScrollRef} />
        <div ref={contentScrollRef} className="w-full overflow-x-auto">
          <table ref={tableRef} className="w-full table-fixed">
            <thead className="whitespace-pre bg-input">
              {normalHeaders.length > 0 && (
                <tr>
                  {normalHeaders.map((header, index) => (
                    <th
                      key={String(header.value)}
                      className={clsx(
                        index === 0 && "pl-2",
                        header.rowStyle,
                        errorList?.length === 0 && header.isShow && "hidden"
                      )}
                      style={{ width: header.width }}>
                      <button
                        type="button"
                        className={clsx(styles.tableHeaderWrapper, header.rowStyle)}>
                        <span className="text-wrap">{header.label}</span>
                      </button>
                    </th>
                  ))}
                </tr>
              )}
              {fullHorizontalHeaders.map((header, idx) => (
                <tr key={`fullHorizontal-${idx}`}>
                  <th colSpan={normalHeaders.length} className={clsx(header.rowStyle)}>
                    <span className="text-wrap">{header.label}</span>
                  </th>
                </tr>
              ))}
            </thead>

            <tbody
              className={clsx(styles.bodyContainer, tableBodyClass, "divide-y divide-gray-700")}>
              {data.map((row, rowIndex) => (
                <React.Fragment key={row.id}>
                  <tr className={clsx("h-full", errorList?.includes(row.id) && "bg-red-300")}>
                    {normalHeaders.map((header, headerIndex) => {
                      let value: any;
                      if (Array.isArray(header.value)) {
                        value = header.value.reduce((acc: Record<string, any>, keyPath: string) => {
                          const keyValue = getNestedValue(row, keyPath);
                          const keyName = keyPath.split(".").pop() ?? keyPath;
                          acc[keyName] = keyValue;
                          return acc;
                        }, {});
                      } else {
                        const keys = header.value.split(".");
                        value = keys.reduce((acc: any, key: string) => acc?.[key], row);
                      }
                      return (
                        <td
                          colSpan={header.colSpan ?? 1}
                          key={String(header.value)}
                          className={clsx(
                            "px-1 py-3 h-full !text-sm",
                            !isShowChecked && headerIndex === 0 && "pl-4",
                            header.rowStyle,
                            errorList?.length === 0 && header.isShow && "hidden",
                            !errorList?.includes(row.id) && header.isShow && "!opacity-0"
                          )}
                          style={{ width: header.width }}>
                          <div
                            className="truncate whitespace-nowrap"
                            title={typeof value === "string" ? value : undefined}>
                            {header.render
                              ? header.render(
                                  value,
                                  rowIndex,
                                  row.id,
                                  primaryClick(value, header),
                                  secondaryClick(value, header)
                                )
                              : value}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {fullHorizontalHeaders.map((header, idx) => (
                    <tr key={`${row.id}-fullHorizontal-${idx}`}>
                      <td colSpan={normalHeaders.length} className={clsx(header.rowStyle)}>
                        {header.render ? (
                          header.render(
                            row,
                            rowIndex,
                            row.id,
                            primaryClick(row, header),
                            secondaryClick(row, header)
                          )
                        ) : (
                          <span className="text-wrap">{header.label || "\u00A0"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div className={clsx("flex flex-col relative", className)}>
      <SimpleScrollbar containerRef={containerRef} />

      <div
        ref={containerRef}
        className="relative overflow-x-auto overflow-y-auto flex-grow h-full rounded-xl bg-background"
        style={{ minHeight: "400px" }}>
        <table ref={tableRef} className="w-full table-auto">
          <thead className="whitespace-pre bg-input">
            <tr>
              {handleSelectAll ? (
                <th className="pl-3">
                  {customSelectAllCheckbox ? (
                    customSelectAllCheckbox({ data, selectedList, selectAllLimit, handleSelectAll })
                  ) : (
                    <Checkbox
                      checked={data.length > 0 && selectedList?.length === data.length}
                      onChange={() => handleSelectAll()}
                    />
                  )}
                </th>
              ) : null}
              {headers
                .filter((item) => item.isShow !== false)
                .map((header, index) => (
                  <th
                    key={header.value as any}
                    className={clsx(
                      index === 0 && "pl-2",
                      header.rowStyle,
                      errorList?.length === 0 && header.isShow && "hidden"
                    )}
                    style={{ width: header.width }}>
                    <button
                      type="button"
                      onClick={() => sortBy(getHeaderSortKey(header))}
                      className={clsx(styles.tableHeaderWrapper, header.rowStyle)}>
                      <span className="text-wrap">{header.label}</span>
                      {!sortDisableList?.includes(getHeaderSortKey(header)) &&
                        (sortKey === getHeaderSortKey(header) ||
                          (sortKey.startsWith("-") &&
                            sortKey.substring(1) === getHeaderSortKey(header))) && (
                          <Icon
                            path={mdiArrowDown}
                            className={clsx(styles.sortIcon, {
                              "rotate-180":
                                sortKey.startsWith("-") &&
                                sortKey.substring(1) === getHeaderSortKey(header),
                              "rotate-0": sortKey === getHeaderSortKey(header)
                            })}
                          />
                        )}
                    </button>
                    {header.subheader && (
                      <div className="flex text-gray-500 mt-1 self-start pl-2 text-xxs">
                        {header.subheader}
                      </div>
                    )}
                  </th>
                ))}
            </tr>
          </thead>

          <tbody className={styles.bodyContainer}>
            {data.length ? (
              data.map((row, index) => {
                const rowKey = row.id ?? `row-${index}`;
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      key={rowKey}
                      className={clsx(tableBodyClass, errorList?.includes(row.id) && "bg-red-300")}
                      style={{ width: rowWidth }}>
                      {isShowChecked && (
                        <td className="pl-3">
                          {customTooltipCheckbox ? (
                            customTooltipCheckbox({
                              rowId: row.id,
                              isSelected,
                              handleChecked,
                              isDisableCheckboxes,
                              selectAllLimit
                            })
                          ) : (
                            <Checkbox
                              checked={isSelected(row.id)}
                              onChange={() => handleChecked(row.id)}
                            />
                          )}
                        </td>
                      )}
                      {headers
                        .filter((item) => item.isShow !== false)
                        .map((header, headerIndex) => {
                          let value: any;
                          if (Array.isArray(header.value)) {
                            value = header.value.reduce(
                              (acc: Record<string, any>, keyPath: string) => {
                                const keyValue = getNestedValue(row, keyPath);
                                const keyName = keyPath.split(".").pop() ?? keyPath;
                                acc[keyName] = keyValue;
                                return acc;
                              },
                              {}
                            );
                          } else {
                            const keys = header.value.split(".");
                            value = keys.reduce((acc: any, key: string) => acc?.[key], row);
                          }
                          return (
                            <td
                              key={header.value as any}
                              onClick={
                                ExpandableView
                                  ? () =>
                                      setExpand((prevState) => ({
                                        ...prevState,
                                        [index]: !prevState[index]
                                      }))
                                  : undefined
                              }
                              className={clsx(
                                "px-1 py-3 h-full !text-sm",
                                !isShowChecked && headerIndex === 0 && "pl-4",
                                header.rowStyle,
                                errorList?.length === 0 && header.isShow && "hidden",
                                !errorList?.includes(row.id) && header.isShow && "!opacity-0"
                              )}
                              style={{ width: header.width }}>
                              <div className="h-full">
                                {header.render
                                  ? header.render(
                                      value,
                                      index,
                                      row.id,
                                      primaryClick(value, header),
                                      secondaryClick(value, header)
                                    )
                                  : value}
                              </div>
                            </td>
                          );
                        })}
                    </tr>
                    {expand[index] && ExpandableView && (
                      <tr key={`${rowKey}-expand`}>
                        <td colSpan={headers.length + (isShowChecked ? 1 : 0)}>
                          <ExpandableView row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={headers.length + (isShowChecked ? 1 : 0)}>
                  {!loading && <div className={styles.emptyDataRow}>No Data</div>}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {infiniteScroll && data.length > 0 && !isSorting && (
          <div className="py-3">
            <RefLoadingSpinner observerRef={loadMoreRef} isLoadingMore={isLoadingMore || loading} />
          </div>
        )}
        {((!infiniteScroll && loading) ||
          (infiniteScroll && data.length === 0 && loading) ||
          isSorting) && (
          <div
            id="loadingSpinner"
            className="absolute inset-0 z-10 flex items-center justify-center bg-gray-400 bg-opacity-30 rounded-lg">
            <Icon path={mdiLoading} spin size={3} className="text-white" />
          </div>
        )}
      </div>

      {!infiniteScroll && totalPageNumbers > 1 && (
        <div className={styles.footer}>
          <Listbox
            value={itemsPerPage}
            onChange={(value) => {
              setPageSize && setPageSize(value);
              handleItemsPerPageChange(value);
            }}
            as="div"
            className="relative">
            <Listbox.Button className={clsx(styles.itemsPerPageButton, "border-r")}>
              {itemsPerPage}
              <Icon path={mdiChevronDown} />
            </Listbox.Button>

            <Listbox.Options className={styles.itemsPerPageOptions}>
              {itemPerPageOptions.map((number) => (
                <Listbox.Option key={number} value={number} className={styles.option}>
                  {number}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox>

          <div className={styles.description}>
            {startIndex} - {lastIndex} items of{" "}
            {totalCount && lastIndex < totalCount ? totalCount : lastIndex} items
          </div>

          <Listbox
            value={pageIndex}
            onChange={(value) => {
              goToPage && goToPage(value);
              setPageIndex(value);
              handlePageChange && handlePageChange(itemsPerPage, value);
            }}
            as="div"
            className="relative">
            <Listbox.Button className={clsx(styles.itemsPerPageButton, "border-l")}>
              {pageIndex}
              <Icon path={mdiChevronDown} />
            </Listbox.Button>
            <Listbox.Options className={styles.itemsPerPageOptions}>
              {[...Array(totalPageNumbers)].map((_, index) => (
                <Listbox.Option key={index + 1} value={index + 1} className={styles.option}>
                  {index + 1}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox>

          <div className={styles.totalPageNumbers}>of {totalPageNumbers} pages</div>

          {loadMore ? (
            <div className="flex-grow flex justify-end items-center mx-2">
              <button
                type="button"
                disabled={!nextLink || loading}
                className="flex items-center justify-center gap-2 py-2 px-6 text-text bg-background hover:bg-popover focus:bg-popover transition-colors duration-300 rounded-md"
                onClick={goNextPage}>
                {loading ? (
                  <>
                    <Icon path={mdiLoading} size={1} spin={1} className="text-primary" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span className="font-medium">Load More</span>
                )}
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                disabled={!prevLink}
                className={styles.menuLeftButton}
                onClick={goPreviousPage}>
                <Icon path={mdiMenuLeft} className="m-auto" />
              </button>
              <button
                type="button"
                disabled={!nextLink}
                className={styles.menuRightButton}
                onClick={goNextPage}>
                <Icon path={mdiMenuLeft} className="m-auto rotate-180" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
